import { makeAutoObservable, runInAction } from 'mobx';
import services from '../services';
import {
    generateLoadEntity,
    generateLoadList,
} from '../utils/mobx';
import { extractErrorMessage } from '../utils/helpers';

class AwareExposeStore {
    dashboard = null;
    campaigns = [];
    campaign = null;
    config = null;

    loadingDashboard = false;
    loadingCampaigns = false;
    loadingCampaign = false;
    savingCampaign = false;
    launchingCampaign = false;
    sendingReminders = false;
    deletingCampaign = false;
    error = null;

    constructor() {
        makeAutoObservable(this);
    }

    setError(error) {
        this.error = error;
    }

    loadDashboard = generateLoadEntity(
        'loadDashboard',
        this,
        'loadingDashboard',
        async (companyId) => {
            return services.Companies.awareExposeAdminService(
                companyId
            ).dashboard();
        },
        'dashboard'
    );

    loadCampaigns = generateLoadList(
        'loadCampaigns',
        this,
        'loadingCampaigns',
        async (companyId) => {
            const result = await services.Companies.awareExposeAdminService(
                companyId
            ).listCampaigns();
            return result.campaigns || [];
        },
        'campaigns'
    );

    loadCampaign = generateLoadEntity(
        'loadCampaign',
        this,
        'loadingCampaign',
        async (companyId, campaignId) => {
            return services.Companies.awareExposeAdminService(
                companyId
            ).getCampaign(campaignId);
        },
        'campaign'
    );

    async createCampaign(companyId, payload) {
        this.savingCampaign = true;
        this.error = null;
        try {
            const c = await services.Companies.awareExposeAdminService(
                companyId
            ).createCampaign(payload);
            runInAction(() => {
                this.campaign = c;
                this.campaigns = [c, ...this.campaigns];
            });
            return c;
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
            throw e;
        } finally {
            runInAction(() => {
                this.savingCampaign = false;
            });
        }
    }

    async updateCampaign(companyId, campaignId, payload) {
        this.savingCampaign = true;
        this.error = null;
        try {
            const c = await services.Companies.awareExposeAdminService(
                companyId
            ).updateCampaign(campaignId, payload);
            runInAction(() => {
                this.campaign = c;
                this.campaigns = this.campaigns.map((x) =>
                    String(x.id) === String(c.id) ? c : x
                );
            });
            return c;
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
            throw e;
        } finally {
            runInAction(() => {
                this.savingCampaign = false;
            });
        }
    }

    async launchCampaign(companyId, campaignId) {
        this.launchingCampaign = true;
        this.error = null;
        try {
            const c = await services.Companies.awareExposeAdminService(
                companyId
            ).launchCampaign(campaignId);
            runInAction(() => {
                this.campaign = c;
                this.campaigns = this.campaigns.map((x) =>
                    String(x.id) === String(c.id) ? c : x
                );
            });
            return c;
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
            throw e;
        } finally {
            runInAction(() => {
                this.launchingCampaign = false;
            });
        }
    }

    async sendReminders(companyId, campaignId) {
        this.sendingReminders = true;
        this.error = null;
        try {
            const r = await services.Companies.awareExposeAdminService(
                companyId
            ).sendReminders(campaignId);
            return r;
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
            throw e;
        } finally {
            runInAction(() => {
                this.sendingReminders = false;
            });
        }
    }

    async cancelCampaign(companyId, campaignId) {
        this.deletingCampaign = true;
        this.error = null;
        try {
            await services.Companies.awareExposeAdminService(
                companyId
            ).cancelCampaign(campaignId);
            runInAction(() => {
                if (
                    this.campaign &&
                    String(this.campaign.id) === String(campaignId)
                ) {
                    this.campaign = {
                        ...this.campaign,
                        status: 'cancelled',
                    };
                }
                this.campaigns = this.campaigns.map((x) =>
                    String(x.id) === String(campaignId)
                        ? { ...x, status: 'cancelled' }
                        : x
                );
            });
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
            throw e;
        } finally {
            runInAction(() => {
                this.deletingCampaign = false;
            });
        }
    }

    async deleteCampaign(companyId, campaignId) {
        this.deletingCampaign = true;
        this.error = null;
        try {
            await services.Companies.awareExposeAdminService(
                companyId
            ).deleteCampaign(campaignId);
            runInAction(() => {
                this.campaigns = this.campaigns.filter(
                    (x) => String(x.id) !== String(campaignId)
                );
                if (
                    this.campaign &&
                    String(this.campaign.id) === String(campaignId)
                ) {
                    this.campaign = null;
                }
            });
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
            throw e;
        } finally {
            runInAction(() => {
                this.deletingCampaign = false;
            });
        }
    }
}

export default AwareExposeStore;
